/** Debug HP scene — lets the developer set player HP to an arbitrary value via +/- steppers and presets. */
import Phaser from 'phaser';
import { crispText, crispButton, setupCrispCamera } from '../../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor, applyButtonHover } from '../../ui/Theme';
import { GAME_WIDTH } from '../../utils/Constants';
import type { GameScene } from '../GameScene';

const HP_STEP = 5;

/**
 * Shows the player's current and max HP with +/- steppers and preset buttons.
 * Clicking SET calls setHp() on the live HealthComponent.
 */
export class DebugSetHPScene extends Phaser.Scene {
  private targetHp: number = 0;
  private targetText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'DebugSetHPScene' });
  }

  create(): void {
    setupCrispCamera(this);
    const width = GAME_WIDTH;

    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    crispText(this, width / 2, 30, 'SET PLAYER HP', TextStyle.debugTitle).setOrigin(0.5);

    const gs = this.scene.get('GameScene') as GameScene;
    const health = gs?.player?.health;
    const currentHp = health?.hp ?? 0;
    const maxHp = health?.max ?? 0;

    this.targetHp = currentHp;

    // Current HP display
    const infoY = 80;
    crispText(this, width / 2, infoY, `Current: ${currentHp} / ${maxHp}`, {
      fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.textSecondary,
    }).setOrigin(0.5);

    // Target HP controls
    const controlY = 130;
    crispText(this, width / 2 - 80, controlY, 'Set to:', TextStyle.body).setOrigin(1, 0.5);

    const minusBtn = crispText(this, width / 2 - 40, controlY, '-', {
      ...TextStyle.body, color: Hex.debugDefault,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.targetText = crispText(this, width / 2, controlY, String(this.targetHp), {
      fontFamily: Font.family, fontSize: Font.sizeBody, color: '#ffffff',
    }).setOrigin(0.5);

    const plusBtn = crispText(this, width / 2 + 40, controlY, '+', {
      ...TextStyle.body, color: Hex.debugDefault,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    minusBtn.on('pointerdown', () => {
      this.targetHp = Math.max(1, this.targetHp - HP_STEP);
      this.targetText.setText(String(this.targetHp));
    });

    plusBtn.on('pointerdown', () => {
      this.targetHp = Math.min(maxHp, this.targetHp + HP_STEP);
      this.targetText.setText(String(this.targetHp));
    });

    applyButtonHover(minusBtn, ButtonColor.debug);
    applyButtonHover(plusBtn, ButtonColor.debug);

    // Preset buttons
    const presetY = 180;
    crispText(this, width / 2, presetY, 'PRESETS:', TextStyle.smallMuted).setOrigin(0.5);

    const presets = [
      { label: '1 HP', value: 1 },
      { label: '25%', value: Math.max(1, Math.round(maxHp * 0.25)) },
      { label: '50%', value: Math.max(1, Math.round(maxHp * 0.5)) },
      { label: 'FULL', value: maxHp },
    ];

    const presetBtnY = presetY + 22;
    const presetSpacing = 80;
    const presetStartX = width / 2 - (presetSpacing * (presets.length - 1)) / 2;

    for (let i = 0; i < presets.length; i++) {
      const preset = presets[i];
      crispButton(this, presetStartX + i * presetSpacing, presetBtnY,
        `${preset.label}`, Font.sizeButtonXs, ButtonColor.debug, () => {
          this.targetHp = preset.value;
          this.targetText.setText(String(this.targetHp));
        });
    }

    // Action buttons
    const btnY = 240;
    crispButton(this, width / 2, btnY, 'SET',
      Font.sizeSmall, ButtonColor.primary, () => this.doSetHP());

    this.input.keyboard!.on('keydown-ESC', () => this.scene.start('DebugSettingsScene'));
  }

  private doSetHP(): void {
    const gs = this.scene.get('GameScene') as GameScene;
    const health = gs?.player?.health;
    if (health) {
      health.setHp(this.targetHp);
    }
    this.scene.start('DebugSettingsScene');
  }
}
